import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { unsubscribeFromPush } from '@api-client/api-client';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { arrayBufferToUrlBase64, normalizeUrlBase64 } from '@utils/webPush';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';

export const MAX_ROTATION_ATTEMPTS_PER_SESSION = 3;

interface UseRotateStaleVapidSubscriptionArgs {
  browserSubscription: PushSubscription | null | undefined;
  vapidKey: string | undefined;
  subscriptionQueryKey: QueryKey;
  backendSubscriptionsQueryKey: QueryKey;
  /**
   * `dataUpdatedAt` from the parent's subscription `useQuery`. Feeds the
   * rotation effect's deps so it can re-run when invalidation refetches the
   * subscription — even if the refetch returns the same stale sub (e.g. the
   * browser unsubscribe rejected and `pushManager.getSubscription()` keeps
   * handing back the stale value). Without this signal, `isStale` stays
   * `true`, deps don't change, and the per-session attempt cap below would
   * be dead code that fires at most once.
   */
  subscriptionDataUpdatedAt: number;
}

interface UseRotateStaleVapidSubscriptionReturn {
  isStale: boolean;
  /**
   * Latches `true` once `MAX_ROTATION_ATTEMPTS_PER_SESSION` rotations have
   * failed in a row. The subscription is stuck in a state no JS-only retry
   * can clear — per the W3C Push API, calling `pushManager.subscribe()` with
   * a different `applicationServerKey` while the existing sub persists rejects
   * with `InvalidStateError`, and the only thing that failed in the first
   * place was `unsubscribe()`. The renderer's `PushSubscription` handle clears
   * on a hard reload (or successful `unsubscribe()`).
   *
   * Currently consumed by the `web_push_rotation_failed` Amplitude event in
   * the cap branch below for production telemetry. A user-facing reload
   * prompt is tracked in https://github.com/himeetball/webapp/issues/669 and
   * deferred from this hook so tone-of-voice and prompt-coordination work
   * happens in its own PR.
   */
  rotationFailed: boolean;
}

/**
 * Detect when the browser's PushSubscription is bound to a VAPID public key
 * different from the one the backend currently serves, and self-heal by
 * unsubscribing on both sides + invalidating the subscription query so the
 * existing auto-subscribe flow creates a fresh one.
 *
 * This handles the case where VAPID keys are rotated server-side: the existing
 * subscription is cryptographically useless (push services reject pushes signed
 * with the new private key against the old applicationServerKey), but the
 * browser has no way to know — it only sees a healthy-looking subscription.
 *
 * Multi-tab race (success path): two tabs detecting the mismatch simultaneously
 * will both unsubscribe and re-subscribe. The PushManager API returns the same
 * subscription on duplicate subscribe calls within a single browser, so they
 * end up with one final shared subscription. Backend dedupes by endpoint via
 * push_subscription_crud.upsert_subscription, so duplicate POSTs are safe.
 *
 * Multi-tab race (failure path): the per-session cap is per-tab, not per-user.
 * N stale tabs × `MAX_ROTATION_ATTEMPTS_PER_SESSION` attempts = up to 3N
 * backend cleanup calls within seconds. Acceptable for the rare
 * rotation-with-failure path; if real telemetry shows backend strain, move
 * the counter to sessionStorage so siblings see exhausted state.
 *
 * Failure handling: if either unsubscribe step rejects, invalidation still
 * runs (in `finally`) so detection can re-fire; a per-session attempts cap
 * prevents an infinite loop if rotation keeps failing. Once the cap fires,
 * `rotationFailed` latches true so a UI surface can prompt a hard reload —
 * no JS-only path can clear the dead subscription (per W3C Push API,
 * `pushManager.subscribe()` with a different `applicationServerKey` rejects
 * `InvalidStateError` while the existing sub persists).
 */
export const useRotateStaleVapidSubscription = ({
  browserSubscription,
  vapidKey,
  subscriptionQueryKey,
  backendSubscriptionsQueryKey,
  subscriptionDataUpdatedAt,
}: UseRotateStaleVapidSubscriptionArgs): UseRotateStaleVapidSubscriptionReturn => {
  const queryClient = useQueryClient();
  // Wrap the backend cleanup call in a mutation so we go through the standard
  // TanStack Query layer (codebase rule: never call API functions directly).
  // The mutation gives us DevTools observability and a stable `mutateAsync`
  // reference; we don't read `isPending`/`error`/`data` because the rotation
  // effect runs as fire-and-forget peer to `sub.unsubscribe()` inside
  // `Promise.allSettled`, with its own attempt cap, cancellation flag, and
  // `finally`-block invalidation handling the recovery contract.
  const { mutateAsync: cleanupBackendSubscription } = useMutation({
    mutationFn: ({ endpoint }: { endpoint: string }) => unsubscribeFromPush({ endpoint }),
  });
  // Counter persists across successful rotations within a session — design
  // choice. Rotations are months-apart in production, so the chance of three
  // legitimate rotations in one session is vanishingly small. If real telemetry
  // shows this hitting the cap on healthy sessions, reset on success in `finally`.
  const attemptsRef = useRef(0);
  // Endpoint we last warned about for the missing-applicationServerKey case
  // (see fold-in inside the rotation effect's `!isStale` early-return). Using
  // a ref keyed on endpoint dedupes the warn so refetches that bump
  // `subscriptionDataUpdatedAt` (which now drives effect re-runs) don't spam
  // identical lines for the same dead subscription.
  const warnedForMissingKeyEndpointRef = useRef<string | null>(null);
  const [rotationFailed, setRotationFailed] = useState(false);
  const { trackEvent } = useAmplitudeTracking();

  // Primitive identifier for the current subscription. `endpoint` is a unique
  // URL string per `PushSubscription`: stable across React Query's structural
  // sharing (no false re-runs) and flips on null → new sub after a rotation
  // (catches identity changes the way an object-identity dep would). Used in
  // place of the full `browserSubscription` object in effect deps below to
  // satisfy the "primitive deps in useEffect" rule (CLAUDE.md, SKILL.md).
  const browserSubscriptionEndpoint = browserSubscription?.endpoint ?? null;

  const isStale = useMemo(() => {
    if (!browserSubscription || !vapidKey) return false;
    const existingKey = browserSubscription.options.applicationServerKey;
    if (!existingKey) return false;
    return arrayBufferToUrlBase64(existingKey) !== normalizeUrlBase64(vapidKey);
  }, [browserSubscription, vapidKey]);

  useEffect(() => {
    if (!isStale) {
      // Fold-in for the missing-`applicationServerKey` case: a sub without
      // it can't be VAPID-validated, so `isStale` stays false and rotation
      // never fires — the user is stuck on a dead sub forever. Lifted out
      // of a standalone effect (which violated "useEffect for external
      // system sync only") into this rotation effect's early-return path,
      // and deduped via `warnedForMissingKeyEndpointRef` so the wider
      // rotation deps don't fire the warn on every refetch tick.
      if (browserSubscription && !browserSubscription.options.applicationServerKey && vapidKey) {
        if (warnedForMissingKeyEndpointRef.current !== browserSubscriptionEndpoint) {
          warnedForMissingKeyEndpointRef.current = browserSubscriptionEndpoint;
          logger.warn(
            'Web push: subscription has no applicationServerKey, cannot validate VAPID rotation',
          );
        }
      }
      return;
    }

    if (attemptsRef.current >= MAX_ROTATION_ATTEMPTS_PER_SESSION) {
      // Cap-hit side effects all live behind the same `!rotationFailed`
      // gate: the warn log, the state latch, and the analytics emit. Without
      // this, the `setRotationFailed(true)` re-render re-runs the effect (the
      // bit is in deps) and fires the warn a second time.
      if (!rotationFailed) {
        logger.warn(
          'Web push: VAPID rotation attempts exhausted for this session, skipping further attempts',
        );
        setRotationFailed(true);
        // Production observability: webapp doesn't ship Sentry, so analytics
        // is Amplitude. `useAmplitudeTracking().trackEvent` queues events
        // pre-init (Amplitude is deferred until requestIdleCallback after
        // window.load — the cap-hit can race with that) and adds the
        // standard base payload (path, session_id, attribution, auth state).
        trackEvent(ANALYTICS_EVENTS.web_push_rotation_failed, {
          attempts: MAX_ROTATION_ATTEMPTS_PER_SESSION,
        });
      }
      return;
    }
    attemptsRef.current += 1;

    let cancelled = false;

    // Capture the subscription in the closure — the effect re-runs whenever
    // `browserSubscriptionEndpoint` flips (sub replaced) or
    // `subscriptionDataUpdatedAt` flips (refetch with structural sharing), so
    // the captured value is always the freshest one for this rotation cycle.
    // Avoid `useRef` writes in render (see meetball-react-patterns/SKILL.md,
    // "Rules the compiler relies on").
    const sub = browserSubscription;

    const rotate = async (): Promise<void> => {
      if (!sub) return;

      logger.info('Web push: VAPID key mismatch detected, rotating subscription');

      try {
        // Run cleanup in parallel — backend cleanup never depended on the
        // browser unsubscribe completing first. The orphan-resync effect
        // early-returns when isStale is true, so there's no scenario where
        // ordering matters for recovery.
        const [browserResult, backendResult] = await Promise.allSettled([
          sub.unsubscribe(),
          cleanupBackendSubscription({ endpoint: sub.endpoint }),
        ]);

        if (cancelled) return;

        if (browserResult.status === 'rejected') {
          logger.error(
            'Web push: browser unsubscribe failed during rotation:',
            sanitizeError(browserResult.reason),
          );
        }
        if (backendResult.status === 'rejected') {
          // sanitizeError strips the raw Axios `config`/`request` (which can carry
          // auth headers and request bodies) down to {name, message, status, detail}.
          // Logged at `error`: a failed cleanup leaves the dead endpoint in the
          // backend until the push service eventually 410s — symmetric severity
          // with the browser-side rejection above.
          logger.error(
            'Web push: backend cleanup of stale subscription failed:',
            sanitizeError(backendResult.reason),
          );
        }
      } finally {
        if (!cancelled) {
          // Always invalidate — even on failure — so detection re-runs and
          // either succeeds or hits the attempt cap. Invalidate the backend
          // subscriptions query too: the orphan-resync flow consults it, and
          // its `gcTime: 0` doesn't help here since the cached snapshot may
          // still hold the now-deleted endpoint.
          void queryClient.invalidateQueries({ queryKey: subscriptionQueryKey });
          void queryClient.invalidateQueries({ queryKey: backendSubscriptionsQueryKey });
        }
      }
    };

    void rotate();

    return () => {
      cancelled = true;
    };
    // Two complementary refetch signals are intentional:
    // - `browserSubscriptionEndpoint`: catches identity changes (e.g. null →
    //   new sub after a successful rotation) via a primitive URL string. This
    //   replaces a previous `browserSubscription` (object) dep — endpoint is a
    //   1-to-1 proxy for sub identity in the structural-share-aware case, and
    //   the closure-captured `sub` above stays fresh because the closure is
    //   re-created on every effect run.
    // - `subscriptionDataUpdatedAt`: catches the failure path where a refetch
    //   returns a structurally-equal sub (TanStack Query's structural sharing
    //   preserves identity), which is exactly what happens when browser
    //   unsubscribe rejected and `pushManager.getSubscription()` keeps handing
    //   back the stale value. Without it, the per-session attempt cap above
    //   would regress to "fires once per `isStale` flip".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isStale,
    queryClient,
    subscriptionQueryKey,
    backendSubscriptionsQueryKey,
    subscriptionDataUpdatedAt,
    browserSubscriptionEndpoint,
    vapidKey,
    rotationFailed,
    trackEvent,
    cleanupBackendSubscription,
    // `browserSubscription` (object) intentionally omitted — see the comments
    // above. `browserSubscriptionEndpoint` covers identity-change re-runs; the
    // object itself is closure-captured for `sub.unsubscribe()` and the
    // missing-applicationServerKey fold-in.
  ]);

  return { isStale, rotationFailed };
};
